import * as React from "react";
import { useState } from "react";
import { GuardrailPill, Icon, Pill, actorClass } from "@ardept/design-system";
import type { EventDto } from "../api/types.js";

/** D-EVENT-01: closed-vocabulary discriminated-union renderer with explicit
 *  fallback for unknown event_types (forward-compatible with future engine
 *  additions; no scary warning band on unknown types). */

type KnownEventType =
  | "workflow_started"
  | "form_submitted"
  | "agent_decided"
  | "tool_called"
  | "tool_returned"
  | "agent_run_completed"
  | "guardrail_verdict_set"
  | "ask_human"
  | "state_entered"
  | "approval_given"
  | "approval_rejected"
  | "workflow_completed"
  | "workflow_failed"
  | "inbound_sender_unverified";

const KNOWN_EVENT_TYPES: ReadonlySet<string> = new Set<KnownEventType>([
  "workflow_started",
  "form_submitted",
  "agent_decided",
  "tool_called",
  "tool_returned",
  "agent_run_completed",
  "guardrail_verdict_set",
  "ask_human",
  "state_entered",
  "approval_given",
  "approval_rejected",
  "workflow_completed",
  "workflow_failed",
  "inbound_sender_unverified",
]);

// CON-04 canonical guardrail names (locked)
const CANONICAL_GUARDRAILS = [
  "no-pii-leak",
  "tone-professional",
  "claims-cited",
  "scope-in-framework",
] as const;

// ── Helper: pad seq to 4 digits ──────────────────────────────────
function seqStr(n: number): string {
  return String(n).padStart(4, "0");
}

// ── Helper: format ISO timestamp as YYYY-MM-DD HH:MM:SSZ (UTC) ──
function fmtTime(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}Z`;
}

// ── Helper: one-line preview of a payload ───────────────────────
function previewLine(payload: unknown): string {
  const s = JSON.stringify(payload);
  if (!s) return "";
  if (s.length <= 80) return s;
  return s.slice(0, 79) + "…";
}

// ── Helper: extract tool_use_id from payload (W-4 fix) ──────────
// Verified against OrchestratorEngine.cs:805-838: payload key is `tool_use_id`
// (snake_case), NOT `tool_call_id` or a top-level event property.
function extractToolUseId(event: EventDto): string | undefined {
  if (typeof event.payload !== "object" || event.payload === null) return undefined;
  const p = event.payload as Record<string, unknown>;
  return typeof p["tool_use_id"] === "string" ? p["tool_use_id"] : undefined;
}

// ── Tool-pair data structures ────────────────────────────────────
interface ToolPair {
  kind: "pair";
  call: EventDto;
  ret: EventDto;
}
interface OrphanCall {
  kind: "orphan";
  call: EventDto;
}
interface SingleEvent {
  kind: "single";
  evt: EventDto;
}
type TimelineRow = ToolPair | OrphanCall | SingleEvent;

// ── Pair tool_called + tool_returned events ─────────────────────
function pairToolEvents(events: EventDto[]): TimelineRow[] {
  const out: TimelineRow[] = [];
  const skip = new Set<number>();

  for (let i = 0; i < events.length; i++) {
    if (skip.has(i)) continue;
    const e = events[i]!;

    if (e.event_type === "tool_called") {
      const callId = extractToolUseId(e);
      if (callId !== undefined) {
        const matchIdx = events.findIndex(
          (x, k) =>
            k > i &&
            x.event_type === "tool_returned" &&
            extractToolUseId(x) === callId,
        );
        if (matchIdx >= 0) {
          skip.add(matchIdx);
          out.push({ kind: "pair", call: e, ret: events[matchIdx]! });
          continue;
        }
      }
      // No tool_use_id or no matching tool_returned: orphan
      out.push({ kind: "orphan", call: e });
      continue;
    }

    // tool_returned that was already consumed by a pair: skip
    if (e.event_type === "tool_returned" && skip.has(i)) continue;

    out.push({ kind: "single", evt: e });
  }

  return out;
}

// ── Per-type bespoke renderer components ────────────────────────

function EventRowWrapper({
  seq,
  ts,
  actor,
  eventType,
  children,
  expandable,
  payload,
}: {
  seq: number;
  ts: string;
  actor: string;
  eventType: string;
  children?: React.ReactNode;
  expandable?: boolean;
  payload?: unknown;
}): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="evt"
      onClick={expandable ? () => setOpen((s) => !s) : undefined}
      style={expandable ? { cursor: "pointer" } : undefined}
    >
      <span className="evt__seq">{seqStr(seq)}</span>
      <span className="evt__when">{fmtTime(ts)}</span>
      <div className="evt__main">
        <div className="evt__line1">
          <span className="evt__type">{eventType}</span>
          <span className={`evt__actor ${actorClass(actor)}`}>{actor}</span>
        </div>
        {children}
        {expandable && payload !== undefined && (
          <div className="evt__preview">{previewLine(payload)}</div>
        )}
        {expandable && open && payload !== undefined && (
          <pre className="evt__expanded">{JSON.stringify(payload, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}

// workflow_started
function WorkflowStartedCard({ event }: { event: EventDto }): JSX.Element {
  const p = event.payload as Record<string, unknown>;
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="workflow_started"
      expandable
      payload={event.payload}
    >
      <div className="evt__preview">
        Workflow: <span className="mono">{String(p["workflow"] ?? "")}</span> {p["version"] ? `v${String(p["version"])}` : ""}
        {p["tenant_slug"] ? <> | tenant: <span className="mono">{String(p["tenant_slug"])}</span></> : ""}
      </div>
    </EventRowWrapper>
  );
}

// form_submitted
function FormSubmittedCard({ event }: { event: EventDto }): JSX.Element {
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="form_submitted"
      expandable
      payload={event.payload}
    >
      <div className="evt__preview">Form submitted</div>
    </EventRowWrapper>
  );
}

// agent_decided
function AgentDecidedCard({ event }: { event: EventDto }): JSX.Element {
  const p = event.payload as Record<string, unknown>;
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="agent_decided"
      expandable
      payload={event.payload}
    >
      {p["decision"] ? (
        <div className="evt__preview">Decision: {String(p["decision"])}</div>
      ) : null}
      {p["rationale"] ? (
        <div className="evt__preview" style={{ fontStyle: "italic", color: "var(--ard-text-2)" }}>
          {String(p["rationale"])}
        </div>
      ) : null}
    </EventRowWrapper>
  );
}

// agent_run_completed
function AgentRunCompletedCard({ event }: { event: EventDto }): JSX.Element {
  const p = event.payload as Record<string, unknown>;
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="agent_run_completed"
      expandable
      payload={event.payload}
    >
      <div className="evt__preview">
        Cost: ${typeof p["cost_usd"] === "number" ? p["cost_usd"].toFixed(4) : "?"}
        {p["model_id"] ? ` | model: ${String(p["model_id"])}` : ""}
        {typeof p["input_tokens"] === "number" && typeof p["output_tokens"] === "number"
          ? ` | tokens: ${p["input_tokens"]}+${p["output_tokens"]}`
          : ""}
      </div>
    </EventRowWrapper>
  );
}

// guardrail_verdict_set: renders 4 canonical GuardrailPill row (CON-04)
function GuardrailVerdictCard({ event }: { event: EventDto }): JSX.Element {
  const p = event.payload as { verdicts?: Array<{ name: string; result: string; note?: string }> };
  const verdictMap: Record<string, { result: string; note?: string }> = {};
  if (p.verdicts) {
    for (const v of p.verdicts) {
      verdictMap[v.name] = { result: v.result, note: v.note };
    }
  }
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="guardrail_verdict_set"
    >
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
        {CANONICAL_GUARDRAILS.map((name) => {
          const v = verdictMap[name];
          return (
            <GuardrailPill
              key={name}
              name={name}
              verdict={v?.result}
            />
          );
        })}
      </div>
    </EventRowWrapper>
  );
}

// ask_human
function AskHumanCard({ event }: { event: EventDto }): JSX.Element {
  const p = event.payload as Record<string, unknown>;
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="ask_human"
      expandable
      payload={event.payload}
    >
      {p["question"] ? (
        <div className="evt__preview" style={{ fontStyle: "italic" }}>
          {String(p["question"])}
        </div>
      ) : null}
    </EventRowWrapper>
  );
}

// state_entered
function StateEnteredCard({ event }: { event: EventDto }): JSX.Element {
  const p = event.payload as Record<string, unknown>;
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="state_entered"
      expandable
      payload={event.payload}
    >
      {p["from"] !== undefined && p["to"] !== undefined ? (
        <div className="evt__preview">
          <span className="mono">{String(p["from"])}</span>
          {" → "}
          <span className="mono">{String(p["to"])}</span>
        </div>
      ) : null}
    </EventRowWrapper>
  );
}

// approval_given / approval_rejected (AUD-03: operator email + rationale + ISO ts)
function ApprovalCard({
  event,
  kind,
}: {
  event: EventDto;
  kind: "given" | "rejected";
}): JSX.Element {
  const p = event.payload as Record<string, unknown>;
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType={kind === "given" ? "approval_given" : "approval_rejected"}
      expandable
      payload={event.payload}
    >
      {p["rationale"] ? (
        <div className="evt__preview">Rationale: {String(p["rationale"])}</div>
      ) : null}
    </EventRowWrapper>
  );
}

// workflow_completed
function WorkflowCompletedCard({ event }: { event: EventDto }): JSX.Element {
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="workflow_completed"
      expandable
      payload={event.payload}
    >
      <div className="evt__preview">Workflow completed</div>
    </EventRowWrapper>
  );
}

// workflow_failed
function WorkflowFailedCard({ event }: { event: EventDto }): JSX.Element {
  const p = event.payload as Record<string, unknown>;
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="workflow_failed"
      expandable
      payload={event.payload}
    >
      {p["reason"] ? (
        <div className="evt__preview">Reason: {String(p["reason"])}</div>
      ) : null}
    </EventRowWrapper>
  );
}

// inbound_sender_unverified (card #3362) — an inbound email from an address the job does not know.
// The mail itself landed (its chat_message_received row is beside this one); this line names the
// sender for the operator. Operator-visible only.
function InboundSenderUnverifiedCard({ event }: { event: EventDto }): JSX.Element {
  const p = (event.payload ?? {}) as Record<string, unknown>;
  const sender = typeof p["sender_email"] === "string" ? p["sender_email"] : "an unknown address";
  return (
    <EventRowWrapper
      seq={event.seq}
      ts={event.created_at}
      actor={event.actor}
      eventType="inbound_sender_unverified"
      expandable
      payload={event.payload}
    >
      <div className="evt__preview">
        <Pill tone="bad">Unverified sender</Pill>{" "}
        Email from <span className="mono">{sender}</span>: not a party on this job and not an
        address it has written to. The message was kept on the job.
      </div>
    </EventRowWrapper>
  );
}

// Generic fallback card (D-EVENT-01: unknown event_types render gracefully)
function UnknownEventCard({ event }: { event: EventDto }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="evt" onClick={() => setOpen((s) => !s)} style={{ cursor: "pointer" }}>
      <span className="evt__seq">{seqStr(event.seq)}</span>
      <span className="evt__when">{fmtTime(event.created_at)}</span>
      <div className="evt__main">
        <div className="evt__line1">
          <span className="evt__type">{event.event_type}</span>
          <span className={`evt__actor ${actorClass(event.actor)}`}>{event.actor}</span>
        </div>
        <div className="evt__preview">{previewLine(event.payload)}</div>
        {open && (
          <pre className="evt__expanded">{JSON.stringify(event.payload, null, 2)}</pre>
        )}
      </div>
    </div>
  );
}

// ── Bespoke per-type dispatch ────────────────────────────────────
function EventCard({ event }: { event: EventDto }): JSX.Element {
  if (!KNOWN_EVENT_TYPES.has(event.event_type)) {
    return <UnknownEventCard event={event} />;
  }

  switch (event.event_type as KnownEventType) {
    case "workflow_started":      return <WorkflowStartedCard event={event} />;
    case "form_submitted":        return <FormSubmittedCard event={event} />;
    case "agent_decided":         return <AgentDecidedCard event={event} />;
    case "agent_run_completed":   return <AgentRunCompletedCard event={event} />;
    case "guardrail_verdict_set": return <GuardrailVerdictCard event={event} />;
    case "ask_human":             return <AskHumanCard event={event} />;
    case "state_entered":         return <StateEnteredCard event={event} />;
    case "approval_given":        return <ApprovalCard event={event} kind="given" />;
    case "approval_rejected":     return <ApprovalCard event={event} kind="rejected" />;
    case "workflow_completed":    return <WorkflowCompletedCard event={event} />;
    case "workflow_failed":       return <WorkflowFailedCard event={event} />;
    case "inbound_sender_unverified": return <InboundSenderUnverifiedCard event={event} />;
    // tool_called / tool_returned are handled by pairToolEvents before this dispatch
    case "tool_called":
    case "tool_returned":
      return <UnknownEventCard event={event} />;
  }
}

// Paired tool card (AUD-02: pairs on payload.tool_use_id)
function ToolPairCard({ pair }: { pair: ToolPair | OrphanCall }): JSX.Element {
  if (pair.kind === "orphan") {
    const callPayload = pair.call.payload as Record<string, unknown>;
    return (
      <div className="toolpair">
        <div className="toolpair__shell">
          <div className="orphan-banner">
            <Icon name="alert-triangle" size={14} />
            <span>No tool_returned event recorded: this row is orphaned.</span>
          </div>
          <div className="toolpair__head">
            <span className="toolpair__seq">{seqStr(pair.call.seq)}</span>
            <span className="toolpair__when">{fmtTime(pair.call.created_at)}</span>
            <div className="toolpair__title">
              <span className="toolpair__chain">tool_called</span>
              <span className="toolpair__tool">
                {typeof callPayload["tool"] === "string" ? callPayload["tool"] : ""}
              </span>
            </div>
            <span className="toolpair__metric">
              <span><b>n/a</b></span>
            </span>
          </div>
          <div className="toolpair__body">
            <div className="toolpair__col" style={{ gridColumn: "1 / 3" }}>
              <h5>Args</h5>
              <pre className="toolpair__json">
                {JSON.stringify(callPayload["args"], null, 2)}
              </pre>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Paired
  const callPayload = pair.call.payload as Record<string, unknown>;
  const retPayload = pair.ret.payload as Record<string, unknown>;
  const durationMs = typeof retPayload["duration_ms"] === "number"
    ? retPayload["duration_ms"]
    : null;

  return (
    <div className="toolpair">
      <div className="toolpair__shell">
        <div className="toolpair__head">
          <span className="toolpair__seq">
            {seqStr(pair.call.seq)}{"-"}{seqStr(pair.ret.seq)}
          </span>
          <span className="toolpair__when">{fmtTime(pair.call.created_at)}</span>
          <div className="toolpair__title">
            <span className="toolpair__chain">
              tool_called <span className="arr">{"→"}</span> tool_returned
            </span>
            <span className="toolpair__tool">
              {typeof callPayload["tool"] === "string" ? callPayload["tool"] : ""}
            </span>
          </div>
          <span className="toolpair__metric">
            {durationMs !== null ? (
              <span><b>{durationMs}</b>ms</span>
            ) : null}
          </span>
        </div>
        <div className="toolpair__body">
          <div className="toolpair__col">
            <h5>Args</h5>
            <pre className="toolpair__json">
              {JSON.stringify(callPayload["args"], null, 2)}
            </pre>
          </div>
          <div className="toolpair__col">
            <h5>Result</h5>
            <pre className="toolpair__json">
              {JSON.stringify(retPayload["result"], null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Public interface ──────────────────────────────────────────────
export interface EventTimelineProps {
  /** Events in ascending seq order. Rendered OLDEST AT TOP (kit README). */
  events: EventDto[];
}

export function EventTimeline({ events }: EventTimelineProps): JSX.Element {
  const rows = pairToolEvents(events);

  return (
    <div className="panel-body">
      {rows.map((row, idx) => {
        if (row.kind === "single") {
          return <EventCard key={`evt-${row.evt.seq}`} event={row.evt} />;
        }
        return <ToolPairCard key={`pair-${idx}`} pair={row} />;
      })}
      {rows.length === 0 && (
        <div style={{ padding: "24px 18px", color: "var(--ard-muted)", font: "500 13px/1.5 var(--font-ui)" }}>
          No events yet.
        </div>
      )}
    </div>
  );
}
